import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePermissionsContext } from "../contexts/PermissionsContext";

export function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

export function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/app/log" replace />;
  }

  return children;
}

export function RBACRoute({ menuKey, children }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { canAccessMenu, loading: permsLoading, error: permsError, refresh } = usePermissionsContext();
  const location = useLocation();

  if (authLoading || permsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Stay inside the app shell and say why, instead of silently bouncing to the Daily Log.
  if (permsError) {
    return (
      <div className="p-8 max-w-xl" data-testid="rbac-error">
        <h2 className="text-lg font-semibold mb-2">Couldn't load your permissions</h2>
        <p className="text-sm text-slate-600 mb-4">{permsError}</p>
        <button className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm" onClick={refresh}>
          Retry
        </button>
      </div>
    );
  }

  if (!canAccessMenu(menuKey)) {
    return (
      <div className="p-8 max-w-xl" data-testid="rbac-denied">
        <h2 className="text-lg font-semibold mb-2">You don't have access to this page</h2>
        <p className="text-sm text-slate-600">
          Your role doesn't include the <code>{menuKey}</code> permission. Ask an admin to grant it.
        </p>
      </div>
    );
  }

  return children;
}

export function PublicRoute({ children }) {
  const { isAuthenticated, isAdmin, isTeamLeader, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect admins and team leaders to dashboard, others to daily log
    if (isAdmin || isTeamLeader) {
      return <Navigate to="/app/dashboard" replace />;
    }
    const from = location.state?.from?.pathname || "/app/log";
    return <Navigate to={from} replace />;
  }

  return children;
}
